import { useState, FormEvent } from "react";
import { useSiteContent } from "../../../context/SiteContentContext";
import { submitContact } from "../../../lib/api";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Controls & Automation", href: "#control" },
  { label: "Cooling Systems", href: "#cooling" },
  { label: "Heating Systems", href: "#heating" },
  { label: "About Us", href: "#about" },
  { label: "Contact Us", href: "#easy" },
];

export default function Footer() {
  const { content } = useSiteContent();
  const meta = content.site_meta;

  const [formData, setFormData] = useState({ message: "", name: "", address: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.email || !formData.phone) return;
    if (formData.message.length > 500) return;
    setSubmitting(true);
    const result = await submitContact(formData);
    setSubmitting(false);
    if (result.success) setSubmitted(true);
  };

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const inputStyle: React.CSSProperties = {
    background: "#f7fbff",
    border: "1px solid rgba(74,159,212,0.2)",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "13px",
    color: "#1a3a52",
    outline: "none",
    width: "100%",
  };

  return (
    <footer
      className="w-full relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#dce9f5 0%,#e8f2fa 60%,#dce9f5 100%)" }}
    >
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg,transparent,#4a9fd4,transparent)" }} />

      {/* Decorative orb */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle,rgba(74,159,212,0.1) 0%,transparent 70%)", transform: "translate(30%,-30%)" }} />

      <div className="max-w-7xl mx-auto px-8 py-14 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">

          {/* Col 1 — Brand */}
          <div className="flex flex-col gap-5">
            <img
              src="https://thermalxpert.nyc/wp-content/uploads/2024/03/ThermalExpert-Color-TransperentBG-300x51.png"
              alt="ThermalXpert"
              className="h-9 w-auto object-contain object-left"
            />
            <p className="text-sm leading-relaxed text-gray-500">
              Here to assist metro area of New York City with controls, air conditioning, and heating needs.
            </p>
            <div className="flex items-center gap-2">
              {["ri-facebook-fill", "ri-instagram-line"].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all hover:scale-110 text-gray-400 hover:text-[#4a9fd4]"
                  style={{ background: "rgba(74,159,212,0.08)", border: "1px solid rgba(74,159,212,0.15)" }}
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>

            {/* Contact info */}
            <div className="flex flex-col gap-3 mt-1">
              {[
                { icon: "ri-map-pin-2-fill", text: meta.address },
                { icon: "ri-mail-fill", text: meta.email },
                { icon: "ri-phone-fill", text: meta.phone },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-2.5 text-sm text-gray-500">
                  <i className={`${item.icon} mt-0.5 flex-shrink-0`} style={{ color: "#4a9fd4" }}></i>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Col 2 — Nav links */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xs font-black uppercase tracking-widest" style={{ color: "#2d7ab5" }}>Quick Links</h3>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="text-sm text-left cursor-pointer transition-colors flex items-center gap-2 group text-gray-500 hover:text-[#4a9fd4]"
                >
                  <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "#4a9fd4" }}></i>
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Col 3 — Contact form */}
          <div className="flex flex-col gap-4">
            <h3 className="text-xs font-black uppercase tracking-widest" style={{ color: "#2d7ab5" }}>Contact Us</h3>
            {submitted ? (
              <div
                className="rounded-2xl p-6 text-center"
                style={{ background: "rgba(74,159,212,0.1)", border: "1px solid rgba(74,159,212,0.25)" }}
              >
                <i className="ri-checkbox-circle-fill text-3xl block mb-2" style={{ color: "#4a9fd4" }}></i>
                <p className="text-sm font-bold text-gray-800">Message sent!</p>
                <p className="text-xs mt-1 text-gray-500">We'll be in touch soon.</p>
              </div>
            ) : (
              <form
                id="thermalxpert-contact-form"
                
                onSubmit={handleSubmit}
                className="flex flex-col gap-2.5"
              >
                <textarea
                  name="message"
                  placeholder="Message.."
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  style={{ ...inputStyle, resize: "none" }}
                />
                <div className="grid grid-cols-3 gap-2">
                  <input name="name" placeholder="Name" value={formData.name} onChange={handleChange} style={inputStyle} />
                  <input name="address" placeholder="Address" value={formData.address} onChange={handleChange} style={inputStyle} />
                  <input name="email" type="email" placeholder="Email" required value={formData.email} onChange={handleChange} style={inputStyle} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input name="phone" type="tel" placeholder="Phone" required value={formData.phone} onChange={handleChange} style={inputStyle} />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="col-span-2 text-white text-sm font-black rounded-xl cursor-pointer transition-all hover:scale-105 hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                    style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)", padding: "10px" }}
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="relative z-10 py-4"
        style={{ borderTop: "1px solid rgba(74,159,212,0.15)" }}
      >
        <div className="max-w-7xl mx-auto px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-gray-400">© 2024 ThermalXpert. All rights reserved.</p>
          <p className="text-xs text-gray-400">Brooklyn, NY — Serving the NYC Metro Area</p>
        </div>
      </div>
    </footer>
  );
}
